import * as React from 'react';
import styled from 'styled-components';
import { Button } from '../../Button';
import { COLORS } from '../../styles';
import Avatar, { AvatarContainer } from '../Avatar';
import MenuLink from './MenuLink';
import { UserMenuItem } from '../../types';
import { flowDown, UserMenuSectionListStyling } from '../mobile/CommonStyles';

const Menu = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  background-color: ${COLORS.white};
  box-sizing: border-box;
  display: none;
  min-width: 312px;
  -webkit-animation: ${flowDown} 0.2s ease-in-out;
  animation: ${flowDown} 0.2s ease-in-out;
  position: absolute;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.15);
  border-radius: 4px;
  border: 1px solid ${COLORS.neutral_200};
  z-index: 100;

  .open & {
    display: block;
  }
`;

const MenuSection = styled.li`
  padding: 8px 0;
  position: relative;

  &:not(:first-of-type)::before {
    content: '';
    position: absolute;
    background: ${COLORS.neutral_100};
    height: 1px;
    top: 0;
    left: 16px;
    right: 16px;
  }

  .disabled-link {
    color: #21212150;
    pointer-events: none;
    key-events: none;
  }
`;

const MenuSectionList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;

  ${UserMenuSectionListStyling};
`;

const StyledAvatarContainer = styled(AvatarContainer)`
  margin: 25px 0 0 25px;
`;

const AvatarAndName = styled.div`
  display: flex;
  flex-direction: row;

  h5 {
    margin: 27px 0 0 20px;
    font-style: normal;
    font-weight: bold;
    font-size: 20px;
    line-height: 140%;
    color: ${COLORS.neutral_600};
  }
  span {
    margin: -5px 0 0 20px;
    font-weight: normal;
    font-size: 14px;
    line-height: 140%;
    color: ${COLORS.neutral_600};
    top: -2px;
  }
`;

const NameAndEmail = styled.div`
  display: flex;
  flex-direction: column;
`;

const OrganizationName = styled.span`
  font-weight: bold;
  font-size: 14px;
  color: ${COLORS.neutral_600};
  margin-left: 25px;
`;

interface Props {
  firstName: string;
  lastName: string;
  email: string;
  organizationName: string;
  clickMenuAction: () => void;
  accountSection?: UserMenuItem[];
  organizationSection?: UserMenuItem[];
  supportSection?: UserMenuItem[];
  signOut?: (e: any) => void;
  signOutLabel?: string;
}

const UserMenu = ({ firstName, lastName, email, organizationName, clickMenuAction, accountSection, organizationSection, supportSection, signOut, signOutLabel }: Props) => {
  React.useEffect(() => {
    function handleKeyPress(e: any) {
      if (e.keyCode === 27) {
        clickMenuAction();
      }
    }

    document.addEventListener('keydown', handleKeyPress, false);
    return () => {
      document.removeEventListener('keydown', handleKeyPress, false);
    };
  }, [clickMenuAction]);

  return (
    <Menu onClick={clickMenuAction} role="menu" aria-labelledby="UserMenuButton">
      <AvatarAndName key="AvatarAndNameSection">
        <StyledAvatarContainer hidePadding={true}>
          <Avatar size={48} firstName={firstName} lastName={lastName} color={COLORS.primary_500} profileMenuLink="#TODO" />
        </StyledAvatarContainer>
        <NameAndEmail>
          <h5>{`${firstName} ${lastName}`}</h5>
          <span>{email}</span>
        </NameAndEmail>
      </AvatarAndName>
      {accountSection && accountSection?.length > 0 && (
        <MenuSection key="MobileAccountSection">
          <MenuSectionList>
            {accountSection?.map(element => (
              <MenuLink key={element?.to} to={element?.to} menuText={element?.label} Icon={element?.icon} onClick={element?.onClick} />
            ))}
          </MenuSectionList>
        </MenuSection>
      )}

      {organizationSection && organizationSection?.length > 0 && (
        <MenuSection key="MobileOrganizationSection">
          <OrganizationName>{organizationName || ''}</OrganizationName>
          <MenuSectionList>
            {organizationSection?.map(element => (
              <MenuLink key={element?.to} to={element?.to} menuText={element?.label} Icon={element?.icon} onClick={element?.onClick} />
            ))}
          </MenuSectionList>
        </MenuSection>
      )}

      <MenuSection key="MobileSupportSection">
        <MenuSectionList>
          {supportSection?.map(element => (
            <MenuLink key={element?.to} isExternal={element.isExternal} to={element?.to} menuText={element?.label} Icon={element?.icon} onClick={element?.onClick} />
          ))}
          {signOut && (
            <Button
              width="90%"
              className="UserMenuLink"
              variant="secondary"
              size="big"
              onClick={e => {
                e.preventDefault();
                signOut(e);
              }}
              style={{
                fontSize: '19px',
                lineHeight: '23px',
                boxSizing: 'border-box',
                borderRadius: '8px',
                height: '56px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                margin: '8px auto 12px auto',
                width: '90%',
              }}>
              {signOutLabel}
            </Button>
          )}
        </MenuSectionList>
      </MenuSection>
    </Menu>
  );
};

export default UserMenu;
